import { useLoad } from "@redotech/react-util/load";
import TriangleDown from "@redotech/redo-icons/play_down_filled.svg";
import TriangleRight from "@redotech/redo-icons/play_right_filled.svg";
import SearchIcon from "@redotech/redo-icons/search-sm.svg";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import { Provider } from "@redotech/redo-model/order";
import { ProductInfo as ModelProductInfo } from "@redotech/redo-model/product";
import { ProductInfo as StorefrontProductInfo } from "@redotech/shopify-storefront";
import { parseNumericIdFromShopifyId } from "@redotech/shopify-util/id";
import { memo, useEffect, useMemo, useState } from "react";
import useDebounce from "usehooks-ts/dist/esm/useDebounce/useDebounce";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../arbiter-components/input/redo-text-input";
import { RedoModal } from "../arbiter-components/modal/redo-modal";
import { Flex } from "../flex";
import { LoadingRedoAnimation } from "../loading-redo-animation";
import { Text } from "../text";
import { UpsellModalProductRow } from "./product-selection-modal-row";

export interface ProductSelectionLimits {
  maxProducts: number;
  currentSelectedCount: number;
}

export const ProductSelectionModalEntry = memo(
  function ProductSelectionModalEntry<
    T extends {
      productId: string;
      productTitle?: string;
      variantIds: string[];
      variants: ModalVariants[];
    },
    U extends ModalProduct,
  >({
    productSelectionChange,
    customAddProductElement,
    exactlyOneVariant = false,
    forceSelectFirstVariant = false,
    searchProducts,
    multiselect = false,
    selectionLimits,
    provider,
    alreadySelectedProducts,
  }: {
    productSelectionChange: (value: T[]) => void;
    customAddProductElement?: (props: {
      onAdd: () => void;
      disabled?: boolean;
    }) => React.ReactNode;
    exactlyOneVariant?: boolean;
    forceSelectFirstVariant?: boolean;
    searchProducts: ({
      search,
      signal,
      provider,
    }: {
      search: string;
      signal: AbortSignal;
      provider?: Provider;
    }) => Promise<U[] | undefined>;
    multiselect?: boolean;
    selectionLimits?: ProductSelectionLimits;
    provider?: Provider;
    alreadySelectedProducts?: { product: ModalProduct; variantId: string }[];
  }) {
    const [modalOpen, setModalOpen] = useState(false);

    // Calculate if we're at the limit
    const isAtSelectionLimit = selectionLimits
      ? selectionLimits.currentSelectedCount >= selectionLimits.maxProducts
      : false;

    return (
      <Flex dir="column" gap="sm">
        {isAtSelectionLimit && (
          <Text fontSize="xs" fontWeight="regular" textColor="warning">
            Maximum number of products reached
          </Text>
        )}
        {customAddProductElement ? (
          customAddProductElement({
            onAdd: () => setModalOpen(true),
            disabled: isAtSelectionLimit,
          })
        ) : (
          <RedoButton
            disabled={isAtSelectionLimit}
            hierarchy="secondary"
            onClick={() => setModalOpen(true)}
            size="sm"
            text="Add product"
          />
        )}
        <ProductSelectionModal
          alreadySelectedProducts={alreadySelectedProducts}
          exactlyOneVariant={exactlyOneVariant}
          forceSelectFirstVariant={forceSelectFirstVariant}
          multiselect={multiselect}
          open={modalOpen}
          provider={provider}
          searchProducts={searchProducts}
          selectionLimits={selectionLimits}
          setOpen={setModalOpen}
          setSelected={(selected) => {
            const newSelected = selected.map((item) => ({
              productId: item.product.id,
              productTitle: item.product.title,
              variantIds: item.variants.map((v) => v.admin_graphql_api_id),
              variants: item.variants,
            }));

            productSelectionChange(newSelected as T[]);
          }}
        />
      </Flex>
    );
  },
);

export const ProductSelectionModal = memo(function ProductSelectionModal<
  U extends ModalProduct,
>({
  open,
  setOpen,
  setSelected,
  exactlyOneVariant,
  forceSelectFirstVariant = false,
  searchProducts,
  multiselect = false,
  selectionLimits,
  provider,
  alreadySelectedProducts,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  setSelected: (
    selected: { product: ModalProduct; variants: ModalVariants[] }[],
  ) => void;
  exactlyOneVariant?: boolean;
  forceSelectFirstVariant?: boolean;
  searchProducts: ({
    search,
    signal,
    provider,
  }: {
    search: string;
    signal: AbortSignal;
    provider?: Provider;
  }) => Promise<U[] | undefined>;
  multiselect?: boolean;
  selectionLimits?: ProductSelectionLimits;
  provider?: Provider;
  alreadySelectedProducts?: { product: ModalProduct; variantId: string }[];
}) {
  const [searchString, setSearchString] = useState<string>("");
  const debouncedSearch = useDebounce(searchString, 500);

  const [selectedProductsExpanded, setSelectedProductsExpanded] =
    useState<boolean>(true);

  const [modalSelected, setModalSelected] = useState<
    { selectedProduct: ModalProduct; selectedVariants: ModalVariants[] }[]
  >([]);

  // Initialize modalSelected with already-selected products when modal opens
  useEffect(() => {
    if (!open) {
      return;
    }

    if (alreadySelectedProducts && alreadySelectedProducts.length > 0) {
      // Convert from { product, variantId } to { selectedProduct, selectedVariants }
      // Group by product ID since each product can have multiple selected variants
      const productMap = new Map<
        string,
        { selectedProduct: ModalProduct; selectedVariants: ModalVariants[] }
      >();

      for (const item of alreadySelectedProducts) {
        const existing = productMap.get(item.product.id);
        const selectedVariant = item.product.variants.find(
          (v) =>
            parseNumericIdFromShopifyId(v.admin_graphql_api_id) ===
            parseNumericIdFromShopifyId(item.variantId),
        );

        if (existing && selectedVariant) {
          existing.selectedVariants.push(selectedVariant);
        } else if (selectedVariant) {
          productMap.set(item.product.id, {
            selectedProduct: item.product,
            selectedVariants: [selectedVariant],
          });
        }
      }

      setModalSelected(Array.from(productMap.values()));
    } else {
      setModalSelected([]);
    }
  }, [open, alreadySelectedProducts]);

  // Calculate the total number of selected variants (each product-variant pair counts as 1)
  const totalSelectedVariants = useMemo(
    () =>
      modalSelected.reduce(
        (sum, item) => sum + item.selectedVariants.length,
        0,
      ),
    [modalSelected],
  );

  // Calculate if we've reached the selection limit
  const isAtSelectionLimit = selectionLimits
    ? totalSelectedVariants >= selectionLimits.maxProducts
    : false;

  const productsLoad = useLoad<ModalProduct[]>(
    async function (signal) {
      if (!open || debouncedSearch === "") {
        return [];
      }
      const results = await searchProducts({
        search: debouncedSearch,
        provider,
        signal,
      });
      return results ?? [];
    },
    [debouncedSearch, open],
  );

  useEffect(() => {
    if (modalSelected.length > 6) {
      setSelectedProductsExpanded(false);
    }
  }, [modalSelected]);

  return (
    <RedoModal
      contentScrollable
      footerBorder
      headerBorder
      isOpen={open}
      modalSize="md"
      onModalCloseRequested={() => {
        setSearchString("");
        setModalSelected([]);
        setOpen(false);
      }}
      primaryButton={{
        text: "Add product",
        onClick: () => {
          setSelected(
            modalSelected.map((item) => ({
              product: item.selectedProduct,
              variants: item.selectedVariants,
            })),
          );
          setOpen(false);
          setSearchString("");
          setModalSelected([]);
        },
      }}
      secondaryButton={{ text: "Cancel", onClick: () => setOpen(false) }}
      subtitle="Search for a specific product to add"
      title="Add Product"
      TitleIcon={TagIcon}
    >
      <Flex dir="column" gap="md" w="full">
        <Flex dir="column" gap="md" pb="lg" w="full">
          <Flex align="center" dir="row" justify="space-between" w="full">
            <Text fontSize="xs" fontWeight="semibold" textColor="tertiary">
              {`Selected products (${modalSelected.length})`}
            </Text>
            <RedoButton
              hierarchy="tertiary"
              IconLeading={
                selectedProductsExpanded ? TriangleDown : TriangleRight
              }
              onClick={() =>
                setSelectedProductsExpanded(!selectedProductsExpanded)
              }
              size="sm"
            />
          </Flex>
          {selectedProductsExpanded && modalSelected.length > 0 && (
            <Flex dir="column" gap="md" w="full">
              {modalSelected.map((item) => (
                <UpsellModalProductRow
                  exactlyOneVariant={exactlyOneVariant}
                  forceSelectFirstVariant={forceSelectFirstVariant}
                  isAtSelectionLimit={isAtSelectionLimit}
                  key={item.selectedProduct.id}
                  multiselect={multiselect}
                  product={item.selectedProduct}
                  selected={modalSelected}
                  setSelected={setModalSelected}
                />
              ))}
            </Flex>
          )}
          {isAtSelectionLimit && (
            <Text fontSize="xs" fontWeight="regular" textColor="warning">
              Maximum number of products reached
            </Text>
          )}
        </Flex>
        <Flex pb="lg" w="full">
          <RedoTextInput
            fullWidth
            IconTrailing={() => <SearchIcon height={12} width={12} />}
            placeholder="Search for a specific product..."
            setValue={(string) => {
              setSearchString(string);
            }}
            value={searchString}
          />
        </Flex>
        {productsLoad.pending && <LoadingRedoAnimation />}
        {productsLoad.error && <Text>Error loading products</Text>}
        {!productsLoad.pending && productsLoad.value && (
          <Flex dir="column" gap="md" justify="space-between">
            {productsLoad.value
              .filter(
                (product) =>
                  // Exclude products already selected
                  !modalSelected.some(
                    (item) =>
                      parseNumericIdFromShopifyId(item.selectedProduct.id) ===
                      parseNumericIdFromShopifyId(product.id),
                  ),
              )
              .map((product) => (
                <Flex dir="column" gap="md" key={product.id}>
                  <UpsellModalProductRow
                    exactlyOneVariant={exactlyOneVariant}
                    forceSelectFirstVariant={forceSelectFirstVariant}
                    isAtSelectionLimit={isAtSelectionLimit}
                    key={product.id}
                    multiselect={multiselect}
                    product={product}
                    selected={modalSelected}
                    setSelected={setModalSelected}
                  />
                </Flex>
              ))}
          </Flex>
        )}
      </Flex>
    </RedoModal>
  );
});

export function storefrontProductToModalProduct(
  product: StorefrontProductInfo,
): ModalProduct {
  return {
    ...product,
    image: { src: product.featuredImage?.url ?? "", altText: "" },
    images: product.images.nodes.map((i) => ({ src: i.url, altText: "" })),
    variants: product.variants.nodes.map((v) => ({
      admin_graphql_api_id: v.id,
      inventory_quantity: v.quantityAvailable ?? 0,
      sku: v.sku,
      title: v.title,
      requires_shipping: v.requiresShipping,
      price: v.price.amount,
      image: { src: v.image?.url ?? "", altText: "" },
      productTitle: product.title,
    })),
  };
}

export function productInfoToModalProduct(
  product: ModelProductInfo,
): ModalProduct {
  return {
    id: product.id,
    title: product.name,
    description: product.description,
    handle: "",
    image: { src: product.image?.url ?? "", altText: product.name },
    images: [{ src: product.image?.url ?? "", altText: "" }],
    tags: product.tags,
    variants: product.variants.map((v) => ({
      admin_graphql_api_id: v.id,
      inventory_quantity: v.quantityAvailable ?? 0,
      sku: v.sku,
      title: v.name,
      price: v.price,
      image: { src: v.image?.url ?? "", altText: v.name },
      productTitle: product.name,
    })),
  };
}

export interface ModalProduct {
  id: string;
  title: string;
  description: string;
  handle: string;
  image: { src: string | null; altText: string | null };
  images: Array<{ src: string | null; altText: string | null }>;
  body_html?: string;
  tags: string[];
  variants: ModalVariants[];
  onlineStoreUrl?: string;
}

export interface ModalVariants {
  admin_graphql_api_id: string;
  inventory_quantity?: number;
  sku?: string | null;
  title: string;
  requires_shipping?: boolean;
  price: string;
  image: { src: string | null; altText: string | null };
  productTitle?: string;
}
